// src/utils/finance.js
// Pure calculation functions — no imports, no side-effects

// ── Formatters ─────────────────────────────────────────────
export const fmt = {
  dollar: n => {
    if (n == null || isNaN(n)) return "—";
    if (Math.abs(n) >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
    if (Math.abs(n) >= 1e9)  return `$${(n / 1e9).toFixed(2)}B`;
    if (Math.abs(n) >= 1e6)  return `$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3)  return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(2)}`;
  },
  pct: (n, d = 1) => (n == null || isNaN(n)) ? "—" : `${(+n).toFixed(d)}%`,
  num: (n, d = 2) => (n == null || isNaN(n)) ? "—" : (+n).toFixed(d),
};

// ── Valuation scoring ──────────────────────────────────────
// Returns 0–100 where 100 = most attractive
export function getValuationScore(stock) {
  let score = 50;

  // P/E (lower = better)
  if (stock.pe != null) {
    if      (stock.pe < 10)  score += 20;
    else if (stock.pe < 15)  score += 12;
    else if (stock.pe < 20)  score += 6;
    else if (stock.pe < 25)  score += 0;
    else if (stock.pe < 35)  score -= 8;
    else                      score -= 18;
  }

  // P/B (lower = better)
  if (stock.pb != null) {
    if      (stock.pb < 1)   score += 14;
    else if (stock.pb < 2)   score += 8;
    else if (stock.pb < 4)   score += 3;
    else if (stock.pb < 7)   score -= 4;
    else                      score -= 12;
  }

  // EV/EBITDA (lower = better)
  if (stock.evEbitda != null) {
    if      (stock.evEbitda < 8)   score += 14;
    else if (stock.evEbitda < 12)  score += 8;
    else if (stock.evEbitda < 18)  score += 2;
    else if (stock.evEbitda < 25)  score -= 6;
    else                            score -= 14;
  }

  // Expected return bonus
  if (stock.expectedReturn >= 15) score += 5;
  if (stock.expectedReturn <= 7)  score -= 5;

  return Math.max(0, Math.min(100, Math.round(score)));
}

export function getValuationLabel(score) {
  if (score >= 65) return { label: "UNDERVALUED", color: "#3ddc84" };
  if (score >= 40) return { label: "FAIR VALUE",  color: "#ffd700" };
  return                   { label: "OVERVALUED",  color: "#ff6b35" };
}

// ── Portfolio metrics ──────────────────────────────────────
export function calcPortfolioMetrics(holdings) {
  if (!holdings.length) return null;
  const total = holdings.reduce((s, h) => s + h.weight, 0);
  if (total === 0) return null;

  const w = holdings.map(h => h.weight / total);

  // FORMULA: Weighted average expected return
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const ret      = holdings.reduce((s, h, i) => s + w[i] * (h.expectedReturn ?? 10), 0);

  // FORMULA: Portfolio volatility — diagonal approximation (zero inter-asset correlation).
  // This is a lower-bound estimate; true vol with positive correlations will be higher.
  // Full w^T·Sigma·w is computed in Optimizer.jsx using live covariance data.
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const vol      = Math.sqrt(holdings.reduce((s, h, i) => s + Math.pow(w[i] * (h.volatility ?? 20), 2), 0));
  const beta     = holdings.reduce((s, h, i) => s + w[i] * (h.beta ?? 1), 0);
  const divYield = holdings.reduce((s, h, i) => s + w[i] * (h.dividend ?? 0), 0);

  // FORMULA: Sharpe = (portfolio_return - risk_free_rate) / portfolio_vol
  // Risk-free rate = 4.5% annual (approximate FRED DGS10 as of audit date)
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const sharpe   = vol > 0 ? (ret - 4.5) / vol : 0;

  // Weighted average valuation ratios (skip nulls in numerator, normalize by weight used)
  const peSum   = holdings.reduce((s, h, i) => s + (h.pe != null ? w[i] * h.pe : 0), 0);
  const peW     = holdings.reduce((s, h, i) => s + (h.pe != null ? w[i] : 0), 0);
  const pbSum   = holdings.reduce((s, h, i) => s + (h.pb != null ? w[i] * h.pb : 0), 0);
  const pbW     = holdings.reduce((s, h, i) => s + (h.pb != null ? w[i] : 0), 0);
  const evSum   = holdings.reduce((s, h, i) => s + (h.evEbitda != null ? w[i] * h.evEbitda : 0), 0);
  const evW     = holdings.reduce((s, h, i) => s + (h.evEbitda != null ? w[i] : 0), 0);

  return {
    ret:      ret.toFixed(1),
    vol:      vol.toFixed(1),
    beta:     beta.toFixed(2),
    sharpe:   sharpe.toFixed(2),
    divYield: divYield.toFixed(2),
    avgPE:    peW > 0 ? (peSum / peW).toFixed(1) : "—",
    avgPB:    pbW > 0 ? (pbSum / pbW).toFixed(1) : "—",
    avgEV:    evW > 0 ? (evSum / evW).toFixed(1) : "—",
  };
}

// ── Weight optimizer ───────────────────────────────────────
export function optimizeWeights(holdings, riskTolerance = 50) {
  if (!holdings.length) return holdings;
  const riskPenalty = 1 - (riskTolerance / 100) * 0.6; // 1.0 (conservative) → 0.4 (aggressive)

  const scores = holdings.map(h => {
    const valScore = getValuationScore(h) / 100;
    const rawScore = (h.expectedReturn ?? 10) - riskPenalty * (h.volatility ?? 20) * 0.3 + valScore * 2;
    return Math.max(0.1, rawScore);
  });

  const total = scores.reduce((s, x) => s + x, 0);
  return holdings.map((h, i) => ({
    ...h,
    weight: parseFloat(((scores[i] / total) * 100).toFixed(1)),
  }));
}

// ── Efficient frontier ─────────────────────────────────────
export function buildEfficientFrontier(universe) {
  const points = [];
  for (let rt = 0; rt <= 100; rt += 5) {
    const optimized = optimizeWeights(universe.slice(0, 20), rt);
    const metrics   = calcPortfolioMetrics(optimized);
    if (metrics) {
      points.push({ vol: parseFloat(metrics.vol), ret: parseFloat(metrics.ret), riskTolerance: rt });
    }
  }
  return points.sort((a, b) => a.vol - b.vol);
}

// ── DCF model ──────────────────────────────────────────────
export function runDCF({
  revenueBase,
  revenueGrowth,
  ebitMargin,
  taxRate,
  reinvestmentRate,
  wacc,
  terminalGrowth,
  forecastYears,
  currentPrice,
}) {
  if (wacc <= terminalGrowth) throw new Error("WACC must exceed terminal growth rate");

  const cashFlows = [];
  let revenue = revenueBase;
  let totalPV = 0;

  for (let yr = 1; yr <= forecastYears; yr++) {
    revenue       *= (1 + revenueGrowth / 100);
    const ebit     = revenue * (ebitMargin / 100);
    const nopat    = ebit * (1 - taxRate / 100);
    const fcf      = nopat * (1 - reinvestmentRate / 100);
    // FORMULA: PV of FCF = FCF / (1 + WACC)^year
    // VERIFIED: Nuvport Math Audit 2026-04-02
    const pv       = fcf / Math.pow(1 + wacc / 100, yr);
    totalPV       += pv;
    cashFlows.push({ year: yr, revenue: +revenue.toFixed(2), ebit: +ebit.toFixed(2), nopat: +nopat.toFixed(2), fcf: +fcf.toFixed(2), pv: +pv.toFixed(2) });
  }

  // FORMULA: Terminal Value (Gordon Growth) = FCF_n+1 / (WACC - g)
  //          where FCF_n+1 = FCF_final * (1 + terminalGrowth)
  // FORMULA: Terminal PV = TerminalValue / (1 + WACC)^n
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const terminalFCF    = cashFlows[cashFlows.length - 1].fcf * (1 + terminalGrowth / 100);
  const terminalVal    = terminalFCF / ((wacc - terminalGrowth) / 100);
  const terminalPV     = terminalVal / Math.pow(1 + wacc / 100, forecastYears);
  const intrinsicValue = totalPV + terminalPV;

  let signal = "HOLD";
  if (currentPrice && intrinsicValue > 0) {
    const upside = (intrinsicValue - currentPrice) / currentPrice;
    if (upside > 0.15)  signal = "BUY";
    if (upside < -0.15) signal = "SELL";
  }

  return {
    cashFlows,
    terminalValue:  +terminalVal.toFixed(2),
    terminalPV:     +terminalPV.toFixed(2),
    intrinsicValue: +intrinsicValue.toFixed(2),
    pvFCF:          +totalPV.toFixed(2),
    terminalPct:    +((terminalPV / intrinsicValue) * 100).toFixed(1),
    signal,
  };
}

// ── DCF sensitivity table ──────────────────────────────────
export function buildSensitivityTable(baseInputs, waccRange, growthRange, steps = 5) {
  const waccStep   = (waccRange[1]  - waccRange[0])  / (steps - 1);
  const growthStep = (growthRange[1] - growthRange[0]) / (steps - 1);

  const waccs   = Array.from({ length: steps }, (_, i) => +(waccRange[0]  + i * waccStep).toFixed(2));
  const growths = Array.from({ length: steps }, (_, i) => +(growthRange[0] + i * growthStep).toFixed(2));

  const table = growths.map(tg =>
    waccs.map(w => {
      try {
        const r = runDCF({ ...baseInputs, wacc: w, terminalGrowth: tg });
        return +r.intrinsicValue.toFixed(2);
      } catch { return null; }
    })
  );

  return { waccs, growths, table };
}

// ── Backtest ───────────────────────────────────────────────
// Fixed: returns all fields Backtest.jsx expects:
//   cumSeries        — [{date, portfolio, benchmark}] cumulative % gain from 0
//   drawdownSeries   — [{date, drawdown}] drawdown % from peak (negative numbers)
//   monthlyHeatmap   — [{year, Jan, Feb, …, Dec, Annual}] array of row objects
//   metrics          — { annReturn, annVol, sharpe, sortino, maxDD,
//                        alpha, beta, winRate, bestMonth, worstMonth }
export function runBacktest({ priceHistory, weights, benchmark }) {
  // ── 1. Align dates ──────────────────────────────────────
  const tickerDates = Object.values(priceHistory).map(s => new Set(s.map(d => d.date)));
  const benchDates  = new Set(benchmark.map(d => d.date));
  const commonDates = [...benchDates]
    .filter(d => tickerDates.every(set => set.has(d)))
    .sort();

  if (commonDates.length < 3) throw new Error("Not enough common dates for backtest");

  // ── 2. Build price lookups ──────────────────────────────
  const lookup = {};
  for (const [ticker, series] of Object.entries(priceHistory)) {
    lookup[ticker] = Object.fromEntries(series.map(d => [d.date, d.close]));
  }
  const benchLookup = Object.fromEntries(benchmark.map(d => [d.date, d.close]));

  // ── 3. Compute monthly returns ──────────────────────────
  const portRets  = [];
  const benchRets = [];

  for (let i = 1; i < commonDates.length; i++) {
    const prev = commonDates[i - 1];
    const curr = commonDates[i];

    let pRet = 0;
    for (const [ticker, w] of Object.entries(weights)) {
      const p0 = lookup[ticker]?.[prev];
      const p1 = lookup[ticker]?.[curr];
      if (p0 && p1 && p0 > 0) pRet += w * ((p1 - p0) / p0);
    }

    const b0 = benchLookup[prev];
    const b1 = benchLookup[curr];
    const bRet = b0 && b1 && b0 > 0 ? (b1 - b0) / b0 : 0;

    portRets.push({ date: curr, r: pRet });
    benchRets.push({ date: curr, r: bRet });
  }

  // ── 4. Cumulative series (% gain from 0, not indexed at 100) ──
  let portVal  = 1;
  let benchVal = 1;
  const cumSeries = portRets.map((pr, i) => {
    portVal  *= (1 + pr.r);
    benchVal *= (1 + benchRets[i].r);
    return {
      date:      pr.date,
      portfolio: +((portVal  - 1) * 100).toFixed(2),
      benchmark: +((benchVal - 1) * 100).toFixed(2),
    };
  });

  // ── 5. Drawdown series ──────────────────────────────────
  let peak   = 1;
  let runVal = 1;
  const drawdownSeries = portRets.map(pr => {
    runVal *= (1 + pr.r);
    if (runVal > peak) peak = runVal;
    const dd = peak > 0 ? -((peak - runVal) / peak) * 100 : 0;
    return { date: pr.date, drawdown: +dd.toFixed(2) };
  });

  // ── 6. Summary metrics ──────────────────────────────────
  const n       = portRets.length;
  const portAvg = portRets.reduce((s, r) => s + r.r, 0) / n;

  // FORMULA: Sample standard deviation (divides by n-1, not n)
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const portStd = n > 1
    ? Math.sqrt(portRets.reduce((s, r) => s + Math.pow(r.r - portAvg, 2), 0) / (n - 1))
    : 0;

  // Annualised
  const finalPortVal = 1 + (cumSeries[cumSeries.length - 1]?.portfolio ?? 0) / 100;
  const annReturn    = +(( Math.pow(finalPortVal, 12 / n) - 1) * 100).toFixed(2);
  const annVol       = +(portStd * Math.sqrt(12) * 100).toFixed(2);

  // Sharpe (Rf = 4.5% annual = 0.375%/month)
  const rf      = 0.045 / 12;
  const sharpe  = portStd > 0 ? +((portAvg - rf) / portStd * Math.sqrt(12)).toFixed(2) : 0;

  // FORMULA: Sortino downside deviation — denominator is total n (Zephyr/Morningstar convention),
  // but the squared excess returns use only downside observations.
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const downRets = portRets.filter(r => r.r < rf).map(r => r.r - rf);
  const downStd  = downRets.length > 0
    ? Math.sqrt(downRets.reduce((s, r) => s + r * r, 0) / n)
    : portStd;
  const sortino  = downStd > 0 ? +((portAvg - rf) / downStd * Math.sqrt(12)).toFixed(2) : 0;

  // Max drawdown
  const maxDD = +(Math.min(...drawdownSeries.map(d => d.drawdown))).toFixed(2);

  // FORMULA: Beta = cov(portfolio, benchmark) / var(benchmark)
  // Using sample covariance (n-1) for both — they cancel in the ratio but
  // consistency with portStd (also n-1) matters when comparing across calculations.
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const benchAvg = benchRets.reduce((s, r) => s + r.r, 0) / n;
  const cov      = n > 1
    ? portRets.reduce((s, pr, i) => s + (pr.r - portAvg) * (benchRets[i].r - benchAvg), 0) / (n - 1)
    : 0;
  const benchVar = n > 1
    ? benchRets.reduce((s, r) => s + Math.pow(r.r - benchAvg, 2), 0) / (n - 1)
    : 0;
  const beta     = benchVar > 0 ? +(cov / benchVar).toFixed(2) : 1;

  // FORMULA: Benchmark CAGR — mirrors portfolio annReturn: use final cumulative value,
  // not arithmetic mean raised to 12. Using mean^12 overstates returns in volatile markets
  // (Jensen's inequality). Both series must use the same compounding formula for alpha
  // to be meaningful.
  // VERIFIED: Nuvport Math Audit 2026-04-02
  const finalBenchVal  = cumSeries[cumSeries.length - 1]
    ? 1 + (cumSeries[cumSeries.length - 1].benchmark ?? 0) / 100
    : 1;
  const benchAnnReturn = +(( Math.pow(finalBenchVal, 12 / n) - 1) * 100).toFixed(2);
  const alpha          = +(annReturn - (4.5 + beta * (benchAnnReturn - 4.5))).toFixed(2);

  // Win rate
  const wins    = portRets.filter(r => r.r > 0).length;
  const winRate = +((wins / n) * 100).toFixed(1);

  // Best / worst month
  const monthlyPcts = portRets.map(r => +(r.r * 100).toFixed(2));
  const bestMonth   = +Math.max(...monthlyPcts).toFixed(2);
  const worstMonth  = +Math.min(...monthlyPcts).toFixed(2);

  // ── 7. Monthly heatmap ──────────────────────────────────
  // Backtest.jsx expects: array of { year, Jan, Feb, ..., Dec, Annual }
  // with null for missing months, values as rounded % numbers
  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const calMap = {};
  for (const pr of portRets) {
    const [yr, mo] = pr.date.split("-");
    if (!calMap[yr]) calMap[yr] = {};
    const monthName = MONTH_NAMES[parseInt(mo, 10) - 1];
    if (monthName) calMap[yr][monthName] = +(pr.r * 100).toFixed(2);
  }

  const monthlyHeatmap = Object.keys(calMap).sort().map(yr => {
    const row = { year: yr };
    let compounded = 1;
    for (const m of MONTH_NAMES) {
      const v = calMap[yr][m] ?? null;
      row[m] = v;
      if (v !== null) compounded *= (1 + v / 100);
    }
    row["Annual"] = +((compounded - 1) * 100).toFixed(2);
    return row;
  });

  // ── 8. Return everything ────────────────────────────────
  return {
    // Charts
    cumSeries,
    drawdownSeries,
    monthlyHeatmap,
    // Metrics object matching Backtest.jsx exactly
    metrics: {
      annReturn:  annReturn.toFixed(2),
      annVol:     annVol.toFixed(2),
      sharpe:     sharpe.toFixed(2),
      sortino:    sortino.toFixed(2),
      maxDD:      maxDD.toFixed(2),
      alpha:      alpha.toFixed(2),
      beta:       beta.toFixed(2),
      winRate:    winRate.toFixed(1),
      bestMonth:  bestMonth.toFixed(2),
      worstMonth: worstMonth.toFixed(2),
    },
    // Legacy fields kept for any other consumers
    portCagr:    annReturn,
    benchCagr:   benchAnnReturn,
    maxDrawdown: Math.abs(maxDD),
    sharpe,
  };
}

/**
 * Merton Jump-Diffusion Monte Carlo simulation.
 * Model: dS/S = (μ - λk̄)dt + σdW + J·dN
 *
 * @param {number} initialValue  - starting portfolio value
 * @param {number} mu            - annualised expected return (e.g. 0.10)
 * @param {number} sigma         - annualised diffusion volatility (e.g. 0.18)
 * @param {number} years         - simulation horizon
 * @param {number} simulations   - number of Monte Carlo paths
 * @param {object} jumpParams    - { lambda, muJ, sigmaJ }
 * @returns {{ paths, percentiles, finalValues }}
 */
export function runMonteCarlo(
  initialValue,
  mu,
  sigma,
  years,
  simulations = 500,
  jumpParams = {}
) {
  const {
    lambda  = 2.0,   // avg jumps per year
    muJ     = -0.05, // mean log-jump size
    sigmaJ  = 0.08,  // std dev of log-jump
  } = jumpParams;

  const steps  = Math.round(years * 252); // daily steps
  const dt     = years / steps;
  const sqrtDt = Math.sqrt(dt);

  // Merton compensation: adjust drift so E[S] = S0 * exp(mu * t)
  const kBar  = Math.exp(muJ + 0.5 * sigmaJ * sigmaJ) - 1;
  const adjMu = mu - lambda * kBar;

  const paths       = [];
  const finalValues = [];

  for (let s = 0; s < simulations; s++) {
    const path = [initialValue];
    let S = initialValue;

    for (let t = 0; t < steps; t++) {
      // Diffusion component
      const z    = normalRandom();
      const diff = (adjMu - 0.5 * sigma * sigma) * dt + sigma * sqrtDt * z;

      // Jump component (Poisson arrivals)
      const numJumps = poissonRandom(lambda * dt);
      let jumpSum = 0;
      for (let j = 0; j < numJumps; j++) {
        jumpSum += muJ + sigmaJ * normalRandom();
      }

      S = S * Math.exp(diff + jumpSum);
      if (s < 200) path.push(Math.round(S * 100) / 100);
    }

    if (s < 200) paths.push(path);
    finalValues.push(S);
  }

  finalValues.sort((a, b) => a - b);

  const pct = (p) => finalValues[Math.floor(p * simulations / 100)];
  const percentiles = {
    p5:  pct(5),
    p10: pct(10),
    p25: pct(25),
    p50: pct(50),
    p75: pct(75),
    p90: pct(90),
    p95: pct(95),
  };

  return { paths, percentiles, finalValues };
}

// FORMULA: Box-Muller transform — produces standard normal N(0,1) from two uniform samples.
// Guards against log(0) by rejecting u === 0.
// VERIFIED: Nuvport Math Audit 2026-04-02
function normalRandom() {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function poissonRandom(lambda) {
  if (lambda <= 0) return 0;
  const L = Math.exp(-lambda);
  let k = 0, p = 1;
  do { k++; p *= Math.random(); } while (p > L);
  return k - 1;
}
